/** SUNTRONIX ONE — Locked Accounts (super_admin brute-force lock manager). */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LockKeyhole, Unlock, RefreshCw, ArrowRight, ShieldAlert } from "lucide-react";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function LockedAccounts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [lockedOnly, setLockedOnly] = useState(false);

  const load = useCallback(async (onlyLocked = lockedOnly) => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/admin/locked-accounts",
        { params: { locked_only: onlyLocked } });
      setData(data); setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load lock records");
    } finally { setLoading(false); }
  }, [lockedOnly]);
  useEffect(() => { load(); }, [load]);

  const unlock = async (row) => {
    setBusyId(row.identifier);
    try {
      await api.post("/auth/admin/unlock", { identifier: row.identifier });
      toast.success(`Unlocked ${row.email}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unlock failed");
    } finally { setBusyId(null); }
  };

  const items = data?.items || [];
  const locked = items.filter((i) => i.locked);

  return (
    <div className="max-w-[1100px] mx-auto space-y-4" data-testid="locked-accounts-page">
      <header className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-rose-900 text-white grid place-items-center">
          <LockKeyhole className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-xl font-heading font-semibold">Locked accounts</div>
          <div className="text-[11px] text-slate-500">
            Brute-force protection: {data?.threshold ?? 5} failed attempts →
            {" "}{data?.lockout_minutes ?? 15} min lock · one-click unlock, no DB access needed
          </div>
        </div>
        <label className="text-[11px] text-slate-600 flex items-center gap-1.5 cursor-pointer"
            data-testid="locked-only-toggle">
          <input type="checkbox" checked={lockedOnly}
              onChange={(e) => { setLockedOnly(e.target.checked); load(e.target.checked); }} />
          Locked only
        </label>
        <button onClick={load} disabled={loading}
            data-testid="locked-accounts-refresh"
            className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-40">
          <RefreshCw className={"h-3 w-3 " + (loading ? "animate-spin" : "")} /> Refresh
        </button>
      </header>

      {error && (
        <div className="border border-rose-200 bg-rose-50 text-rose-800 text-[12px] rounded-lg p-3"
            data-testid="locked-accounts-error">
          <ShieldAlert className="h-4 w-4 inline mr-1" /> {error}
        </div>
      )}

      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold text-slate-700 flex items-center gap-2">
          Failed-login records ({items.length})
          {locked.length > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800"
                data-testid="locked-count-badge">
              {locked.length} currently locked
            </span>
          )}
        </div>
        <table className="w-full text-[11.5px]" data-testid="locked-accounts-table">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Account</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Failed attempts</th>
              <th className="text-left px-3 py-2">Last attempt</th>
              <th className="text-left px-3 py-2">Locked until</th>
              <th className="text-left px-3 py-2">Last IP</th>
              <th className="text-right px-3 py-2 w-28"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td colSpan={7} className="text-center text-[11px] text-slate-400 px-3 py-8">
                No failed-login records — all clear.
              </td></tr>
            )}
            {items.map((r) => (
              <tr key={r.identifier} className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`locked-row-${r.email}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.email}</div>
                  <div className="text-[10px] text-slate-500">
                    {r.user_exists
                      ? `${r.user_name || "—"} · ${r.user_role || ""}`
                      : "no matching user account"}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <span className={"text-[9px] font-semibold px-1.5 py-0.5 rounded " +
                      (r.locked ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800")}>
                    {r.locked ? "LOCKED" : "COUNTING"}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono">{r.count ?? 0} / {data?.threshold ?? 5}</td>
                <td className="px-3 py-2 text-slate-600">{fmtTime(r.last_attempt)}</td>
                <td className="px-3 py-2 text-slate-600">{r.locked ? fmtTime(r.locked_until) : "—"}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{r.last_ip || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => unlock(r)} disabled={busyId === r.identifier}
                      data-testid={`unlock-btn-${r.email}`}
                      className="text-[10.5px] px-2.5 py-1 rounded-md bg-emerald-800 text-white hover:bg-emerald-700 inline-flex items-center gap-1 disabled:opacity-40">
                    <Unlock className="h-3 w-3" />
                    {busyId === r.identifier ? "…" : "Unlock"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-slate-200 rounded-lg bg-white p-3" data-testid="email-aliases-card">
        <div className="text-[11px] font-semibold text-slate-700 mb-2">Login email aliases</div>
        <div className="text-[10px] text-slate-500 mb-2">
          Legacy addresses that transparently resolve to the current account at login —
          muscle-memory typing of an old email will never lock anyone out.
        </div>
        {(data?.aliases || []).length === 0 ? (
          <div className="text-[11px] text-slate-400">No aliases configured.</div>
        ) : (
          <div className="space-y-1">
            {(data?.aliases || []).map((a) => (
              <div key={a.alias} className="flex items-center gap-2 text-[11.5px]"
                  data-testid={`alias-row-${a.alias}`}>
                <span className="font-mono px-1.5 py-0.5 rounded bg-slate-100">{a.alias}</span>
                <ArrowRight className="h-3 w-3 text-slate-400" />
                <span className="font-mono px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800">{a.canonical}</span>
                <span className={"text-[9px] px-1.5 py-0.5 rounded " +
                    (a.active !== false ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500")}>
                  {a.active !== false ? "ACTIVE" : "OFF"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
